import type { AuthSessionResponse, SessionSummary } from '@jaf/contracts';
import { createStore } from 'zustand/vanilla';

/** Estado da sessão no navegador. */
export type SessionStatus =
  'unknown' | 'authenticated' | 'anonymous' | 'forbidden' | 'unavailable' | 'origin-not-allowed';

/** Sessão em memória: o access token nunca vai para localStorage/sessionStorage. */
export interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  expiresAt: string | null;
  session: SessionSummary | null;
  /** Incrementado a cada troca/encerramento de sessão; descarta respostas antigas. */
  generation: number;
  authenticate: (response: AuthSessionResponse) => void;
  end: (status: Exclude<SessionStatus, 'authenticated'>) => void;
}

function sameEntries(previous: readonly string[], next: readonly string[]): boolean {
  const entries = new Set(previous);
  return entries.size === new Set(next).size && next.every((entry) => entries.has(entry));
}

function roleKeys(session: SessionSummary): string[] {
  return session.roles.map((role) => `${role.code}:${role.companyId ?? '*'}`);
}

/** Mesmo usuário com o mesmo contexto de autorização (organização, perfis e permissões). */
function sameAuthorization(previous: SessionSummary | null, next: SessionSummary): boolean {
  return (
    previous !== null &&
    previous.userId === next.userId &&
    previous.organizationId === next.organizationId &&
    sameEntries(roleKeys(previous), roleKeys(next)) &&
    sameEntries(previous.permissions, next.permissions)
  );
}

/** Cria o store de sessão (não persistido). */
export function createSessionStore() {
  return createStore<SessionState>()((set) => ({
    status: 'unknown',
    accessToken: null,
    expiresAt: null,
    session: null,
    generation: 0,
    authenticate: (response) => {
      set((state) => ({
        status: 'authenticated',
        accessToken: response.accessToken,
        expiresAt: response.expiresAt,
        session: response.session,
        generation: sameAuthorization(state.session, response.session)
          ? state.generation
          : state.generation + 1,
      }));
    },
    end: (status) => {
      set((state) => ({
        status,
        accessToken: null,
        expiresAt: null,
        session: null,
        generation: state.generation + 1,
      }));
    },
  }));
}

/** Tipo do store de sessão. */
export type SessionStore = ReturnType<typeof createSessionStore>;
